import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Phone } from "lucide-react";
import { STEPS } from "@/components/spt/panduanSteps";
import { GuideStep } from "@/components/spt/GuideStep";

const thisYear = new Date().getFullYear();
const KEY = (t) => `khn_panduan_${t}`;

function StatusBox({ pph, tahun }) {
  if (!pph) return null;
  const kurang = Number(pph.pph_kurang_bayar || 0), lebih = Number(pph.pph_lebih_bayar || 0);
  const [label, cls, text] = kurang > 0
    ? ["Kurang Bayar", "bg-rose-50 border-rose-200 text-rose-800", `Perkiraan pajak yang masih harus dibayar ${rupiah(kurang)}. Bayar dulu lewat kode billing, baru lapor.`]
    : lebih > 0
      ? ["Lebih Bayar", "bg-sky-50 border-sky-200 text-sky-800", `Perkiraan kelebihan bayar ${rupiah(lebih)}. Anda tetap cukup melapor.`]
      : ["Nihil", "bg-emerald-50 border-emerald-200 text-emerald-800", "Perkiraan tidak ada pajak yang perlu dibayar. Anda cukup melapor (tanpa bayar)."];
  return (
    <div className={`rounded-lg border p-4 ${cls}`} data-testid="guide-status">
      <div className="text-[11px] uppercase tracking-wide opacity-80">Perkiraan status SPT {tahun} dari data Anda</div>
      <div className="text-2xl font-semibold mt-1" data-testid="guide-status-label">{label}</div>
      <p className="text-sm mt-1">{text}</p>
      <p className="text-[11px] mt-2 opacity-80">Angka resmi tetap yang dihitung Coretax di tab Induk.</p>
    </div>
  );
}

export default function PanduanPajak() {
  const [tahun, setTahun] = useState(thisYear - 1);
  const [spt, setSpt] = useState(null);
  const [pph, setPph] = useState(null);
  const [done, setDone] = useState({});
  const [open, setOpen] = useState("siap");

  useEffect(() => {
    try { setDone(JSON.parse(localStorage.getItem(KEY(tahun)) || "{}")); } catch { setDone({}); }
    Promise.all([api.get("/spt-coretax", { params: { tahun } }), api.get("/laporan/pph-op", { params: { tahun } })])
      .then(([a, b]) => { setSpt(a.data); setPph(b.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [tahun]);

  const steps = useMemo(() => STEPS.filter((s) => !s.need || spt?.modul?.[s.need]), [spt]);
  const count = steps.filter((s) => done[s.id]).length;
  const mark = (id) => {
    const next = { ...done, [id]: !done[id] };
    setDone(next); localStorage.setItem(KEY(tahun), JSON.stringify(next));
    if (!done[id]) { const i = steps.findIndex((s) => s.id === id); setOpen(steps[i + 1]?.id || null); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="panduan-pajak-page">
      <PageHeader title="Panduan Bayar Pajak" subtitle="Langkah demi langkah lapor & bayar SPT Tahunan Pribadi di Coretax, dengan bahasa sederhana."
        actions={<Select value={String(tahun)} onValueChange={(v) => setTahun(Number(v))}>
          <SelectTrigger className="w-32" data-testid="guide-tahun"><SelectValue/></SelectTrigger>
          <SelectContent>{[1, 2, 3].map((i) => <SelectItem key={i} value={String(thisYear - i)}>SPT {thisYear - i}</SelectItem>)}</SelectContent>
        </Select>}/>
      <div className="grid gap-3 md:grid-cols-[1.4fr_1fr]">
        <StatusBox pph={pph} tahun={tahun}/>
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2 text-sm text-slate-700">
          <div className="flex items-start gap-2"><CalendarClock size={16} className="mt-0.5 text-amber-600"/><span>Batas lapor SPT {tahun}: <b>31 Maret {tahun + 1}</b>.</span></div>
          <div className="flex items-start gap-2"><Phone size={16} className="mt-0.5 text-[#166db4]"/><span>Masih bingung? Kring Pajak <b>1500200</b> atau datang ke KPP terdekat.</span></div>
        </div>
      </div>
      <div className="space-y-1.5" data-testid="guide-progress">
        <div className="flex justify-between text-[12px] text-slate-500"><span>Progres Anda</span><span>{count} dari {steps.length} langkah</span></div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${steps.length ? (count / steps.length) * 100 : 0}%` }}/></div>
      </div>
      <ol className="space-y-2.5">
        {steps.map((s, i) => (
          <GuideStep key={s.id} no={i + 1} step={s} tahun={tahun} data={spt?.data} done={!!done[s.id]} open={open === s.id}
            onToggle={() => setOpen(open === s.id ? null : s.id)} onDone={() => mark(s.id)}/>
        ))}
      </ol>
      <p className="text-[11px] text-slate-400">Panduan ini ringkasan umum agar mudah diikuti. Tampilan Coretax bisa berubah sewaktu-waktu; ikuti petunjuk di layar Coretax bila berbeda.</p>
    </div>
  );
}
